import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import axios from '../../../../api/axios';
import {
  CONTRACTS_AUTOMATION_ENDPOINT,
  CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from '../../../../consts';
import { ContractAutomationItem, ContractAutomationLevelValue } from '../../../../types/contracts';
import { CounterPriorityProviderValue } from '../../../../types/tenantSettings';
import { useHasPermission } from '../../../../contexts/auth';
import { useTenantSettings } from '../../../../hooks/useTenantSettings';
import { TenantSettingsSection } from '../tenantSettings/tenantSettingsSection.component';
import styles from './tenantSettlementAutomation.module.css';

// The backend represents "no automation"/"inherit tenant default" as null; the
// Select can't take a null value directly, so the empty string stands in for
// it in the UI only.
const MANUAL_VALUE = '';
const INHERIT_VALUE = '';

const AUTOMATION_LEVEL_OPTIONS: { value: ContractAutomationLevelValue | typeof MANUAL_VALUE; label: string }[] = [
  { value: MANUAL_VALUE, label: 'Manual' },
  { value: 'C', label: 'Auto-confirm settlements' },
  { value: 'I', label: 'Auto-confirm and invoice' },
  { value: 'K', label: 'Auto-confirm, invoice and send to KSeF' },
  { value: 'F', label: 'Auto-confirm, invoice, send to KSeF and send to client' },
];

const COUNTER_PRIORITY_PROVIDER_OPTIONS: { value: CounterPriorityProviderValue | typeof INHERIT_VALUE; label: string }[] = [
  { value: INHERIT_VALUE, label: 'Inherit tenant default' },
  { value: 'U', label: 'User' },
  { value: 'E', label: 'Email report' },
  { value: 'P', label: 'PDF' },
];

const toUiValue = (level: ContractAutomationLevelValue | null): string => level ?? MANUAL_VALUE;
const fromUiValue = (value: string): ContractAutomationLevelValue | null =>
  value === MANUAL_VALUE ? null : (value as ContractAutomationLevelValue);

const toCounterPriorityUiValue = (value: CounterPriorityProviderValue | null): string => value ?? INHERIT_VALUE;
const fromCounterPriorityUiValue = (value: string): CounterPriorityProviderValue | null =>
  value === INHERIT_VALUE ? null : (value as CounterPriorityProviderValue);

// ISO date strings ("YYYY-MM-DD") compare correctly lexicographically.
const isContractActive = (endDate: string | null): boolean =>
  !endDate || endDate >= new Date().toISOString().slice(0, 10);

const SCROLL_THRESHOLD_PX = 24;
const SEARCH_DEBOUNCE_MS = 300;
// Long option labels ("Auto-confirm, invoice, send to KSeF and send to client",
// longer still in Polish) would overflow a narrow row, so let the closed Select
// ellipsize and the open menu wrap instead. The menu is free to be wider than
// the field it drops from, so the full label stays readable while choosing.
const SELECT_MENU_ITEM_SX = { whiteSpace: 'normal' as const };

const buildListUrl = (search: string): string => {
  const trimmed = search.trim();
  if (!trimmed) {
    return CONTRACTS_AUTOMATION_ENDPOINT;
  }

  const params = new URLSearchParams({ search: trimmed });
  return `${CONTRACTS_AUTOMATION_ENDPOINT}?${params.toString()}`;
};

export const TenantSettlementAutomation = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ContractAutomationItem[]>([]);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [pendingChanges, setPendingChanges] = useState<
    Record<number, { automation_level?: ContractAutomationLevelValue | null; counter_priority_provider?: CounterPriorityProviderValue | null }>
  >({});
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canView = useHasPermission('settlement.view_contract');
  const canChange = useHasPermission('settlement.change_contract');
  const { settings } = useTenantSettings();
  const defaultCounterPriorityProvider = settings?.equipment_counter_priority_provider ?? null;

  const fetchPage = useCallback(
    async (url: string, replace: boolean) => {
      if (replace) {
        setInitialLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const { data } = await axios.get(url);
        setItems((prev) => (replace ? data.results : [...prev, ...data.results]));
        setNextUrl(data.next);
        if (replace) {
          setPendingChanges({});
        }
      } catch (error) {
        enqueueSnackbar(t('Failed to fetch contracts'), ERROR_SNACKBAR);
      } finally {
        if (replace) {
          setInitialLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [enqueueSnackbar, t]
  );

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchPage(buildListUrl(search), true);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!nextUrl || loadingMore || initialLoading) {
      return;
    }

    const target = event.currentTarget;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - SCROLL_THRESHOLD_PX;

    if (reachedBottom) {
      fetchPage(nextUrl, false);
    }
  };

  const handleLevelChange = (
    contractId: number,
    currentLevel: ContractAutomationLevelValue | null,
    nextLevel: ContractAutomationLevelValue | null
  ) => {
    setPendingChanges((prev) => {
      const next = { ...prev, [contractId]: { ...prev[contractId] } };
      if (nextLevel === currentLevel) {
        delete next[contractId].automation_level;
      } else {
        next[contractId].automation_level = nextLevel;
      }
      if (Object.keys(next[contractId]).length === 0) {
        delete next[contractId];
      }
      return next;
    });
  };

  const handleCounterPriorityProviderChange = (
    contractId: number,
    currentValue: CounterPriorityProviderValue | null,
    nextValue: CounterPriorityProviderValue | null
  ) => {
    setPendingChanges((prev) => {
      const next = { ...prev, [contractId]: { ...prev[contractId] } };
      if (nextValue === currentValue) {
        delete next[contractId].counter_priority_provider;
      } else {
        next[contractId].counter_priority_provider = nextValue;
      }
      if (Object.keys(next[contractId]).length === 0) {
        delete next[contractId];
      }
      return next;
    });
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const handleSave = async () => {
    const payload = Object.entries(pendingChanges).map(([id, changes]) => ({
      id: Number(id),
      ...changes,
    }));

    if (payload.length === 0) {
      return;
    }

    setSaving(true);
    try {
      await axios.patch(CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT, payload);
      enqueueSnackbar(t('Automation settings were successfully saved.'), SUCCESS_SNACKBAR);
      fetchPage(buildListUrl(search), true);
    } catch (error) {
      enqueueSnackbar(t('There was an error, please try again.'), ERROR_SNACKBAR);
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return null;
  }

  return (
    <TenantSettingsSection section="automation">
      <Box className={styles.contracts}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('Contracts')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('Select the automation level and equipment counter priority provider for each contract.')}
        </Typography>

        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('Search by contract number or client name')}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {initialLoading ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('No contracts found')}
            </Typography>
          </Box>
        ) : (
          <Paper
            className={styles.listPaper}
            sx={{ maxHeight: 480, overflow: 'auto' }}
            onScroll={handleScroll}
          >
            <List disablePadding>
              {items.map((contract, index) => {
                const contractChanges = pendingChanges[contract.id];
                const effectiveLevel =
                  contractChanges && 'automation_level' in contractChanges
                    ? contractChanges.automation_level ?? null
                    : contract.automation_level;
                const effectiveCounterPriorityProvider =
                  contractChanges && 'counter_priority_provider' in contractChanges
                    ? contractChanges.counter_priority_provider ?? null
                    : contract.counter_priority_provider;
                const active = isContractActive(contract.end_date);

                return (
                  <React.Fragment key={contract.id}>
                    <ListItem className={styles.listItem}>
                      <Box className={styles.itemInfo}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {[contract.number, contract.client?.name].filter(Boolean).join(' - ')}
                          </Typography>
                          <Chip
                            label={active ? t('Active') : t('Inactive')}
                            size="small"
                            sx={{
                              bgcolor: active ? 'success.light' : 'warning.light',
                              color: active ? 'success.contrastText' : 'warning.contrastText',
                              fontWeight: 600,
                              '& .MuiChip-label': {
                                color: active ? 'success.contrastText' : 'warning.contrastText',
                              },
                            }}
                          />
                        </Box>
                        {contract.internal_name && (
                          <Typography variant="body2" color="text.secondary">
                            {contract.internal_name}
                          </Typography>
                        )}
                      </Box>
                      <Box className={styles.controls}>
                        <FormControl
                          size="small"
                          disabled={!canChange}
                          className={`${styles.control} ${styles.controlWide}`}
                        >
                          <InputLabel id={`automation-level-${contract.id}`} shrink>
                            {t('Automation level')}
                          </InputLabel>
                          <Select
                            labelId={`automation-level-${contract.id}`}
                            // "Manual" and "Inherit tenant default" are stored as
                            // empty strings, so the notch has to be opened by hand -
                            // the field never counts as filled.
                            input={<OutlinedInput notched label={t('Automation level')} />}
                            value={toUiValue(effectiveLevel)}
                            displayEmpty
                            onChange={(event: SelectChangeEvent) =>
                              handleLevelChange(
                                contract.id,
                                contract.automation_level,
                                fromUiValue(event.target.value)
                              )
                            }
                          >
                            {AUTOMATION_LEVEL_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value} sx={SELECT_MENU_ITEM_SX}>
                                {t(option.label)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl
                          size="small"
                          disabled={!canChange}
                          className={styles.control}
                        >
                          <InputLabel id={`counter-priority-${contract.id}`} shrink>
                            {t('Counter priority provider')}
                          </InputLabel>
                          <Select
                            labelId={`counter-priority-${contract.id}`}
                            input={<OutlinedInput notched label={t('Counter priority provider')} />}
                            value={toCounterPriorityUiValue(effectiveCounterPriorityProvider ?? null)}
                            displayEmpty
                            onChange={(event: SelectChangeEvent) =>
                              handleCounterPriorityProviderChange(
                                contract.id,
                                contract.counter_priority_provider,
                                fromCounterPriorityUiValue(event.target.value)
                              )
                            }
                          >
                            {COUNTER_PRIORITY_PROVIDER_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value} sx={SELECT_MENU_ITEM_SX}>
                                {option.value === INHERIT_VALUE && defaultCounterPriorityProvider
                                  ? t('Inherit tenant default ({{provider}})', {
                                      provider: t(
                                        COUNTER_PRIORITY_PROVIDER_OPTIONS.find((o) => o.value === defaultCounterPriorityProvider)
                                          ?.label || defaultCounterPriorityProvider
                                      ),
                                    })
                                  : t(option.label)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </ListItem>
                    {index < items.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Paper>
        )}

        {canChange && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="success" disabled={!hasChanges || saving} onClick={handleSave}>
              {saving ? t('Saving...') : t('Save')}
            </Button>
          </Box>
        )}
      </Box>
    </TenantSettingsSection>
  );
};
